import {
    Address,
    beginCell,
    Builder,
    Cell,
    Contract,
    contractAddress,
    ContractProvider,
    Sender,
    SendMode,
    toNano,
} from 'ton-core';
import { Dao } from './Dao';

export type JettonWalletConfig = {
    balance: bigint;
    ownerAddress: Address;
    daoAddress: Address;
    jettonWalletCode: Cell;
    locked: bigint;
    lockExpiration: Date;
    voteKeeperCode: Cell;
};

export function jettonWalletConfigToCell(config: JettonWalletConfig): Cell {
    return beginCell()
        .storeCoins(config.balance)
        .storeAddress(config.ownerAddress)
        .storeAddress(config.daoAddress)
        .storeRef(config.jettonWalletCode)
        .storeCoins(config.locked)
        .storeUint(Math.round(config.lockExpiration.getTime() / 1000), 48)
        .storeRef(config.voteKeeperCode)
        .endCell();
}

export class JettonWallet implements Contract {
    constructor(readonly address: Address, readonly init?: { code: Cell; data: Cell }) {}

    static createFromAddress(address: Address) {
        return new JettonWallet(address);
    }

    static createFromConfig(config: JettonWalletConfig, code: Cell, workchain = 0) {
        const data = jettonWalletConfigToCell(config);
        const init = { code, data };
        return new JettonWallet(contractAddress(workchain, init), init);
    }

    async sendDeploy(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        options: { jettonAmount: bigint; responseAddress: Address; forwardTonAmount: bigint },
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x178d4519, 32)
                .storeUint(queryId, 64)
                .storeCoins(options.jettonAmount)
                .storeUint(0, 2)
                .storeAddress(options.responseAddress)
                .storeCoins(options.forwardTonAmount)
                .storeUint(0, 1)
                .endCell(),
        });
    }

    async getJettonBalance(provider: ContractProvider) {
        let state = await provider.getState();
        if (state.state.type !== 'active') {
            return 0n;
        }
        let res = await provider.get('get_wallet_data', []);
        return res.stack.readBigNumber();
    }

    async getWalletData(provider: ContractProvider) {
        let res = await provider.get('get_wallet_data', []);
        const balance = res.stack.readBigNumber();
        const ownerAddress = res.stack.readAddress();
        const daoAddress = res.stack.readAddress();
        const jettonWalletCode = res.stack.readCell();

        return {
            balance,
            ownerAddress,
            daoAddress,
            jettonWalletCode,
        };
    }

    async getWalletLockedBalance(provider: ContractProvider) {
        let res = await provider.get('get_locked_balance', []);
        const balance = res.stack.readBigNumber();
        const locked = res.stack.readBigNumber();
        const lockExpiration = res.stack.readNumber();

        return {
            balance,
            locked,
            lockExpiration,
        };
    }

    async getDaoWalletDate(provider: ContractProvider) {
        let res = await provider.get('get_dao_wallet_data', []);
        const balance = res.stack.readBigNumber();
        const ownerAddress = res.stack.readAddress();
        const daoAddress = res.stack.readAddress();
        const jettonWalletCode = res.stack.readCell();
        const locked = res.stack.readBigNumber();
        const lockExpiration = res.stack.readNumber();
        const voteKeeperCode = res.stack.readCell();

        return {
            balance,
            ownerAddress,
            daoAddress,
            jettonWalletCode,
            locked,
            lockExpiration,
            voteKeeperCode,
        };
    }

    static transferMessage(
        jetton_amount: bigint,
        to: Address,
        responseAddress: Address,
        customPayload: Cell | null,
        forward_ton_amount: bigint,
        forwardPayload: Cell | null
    ) {
        return beginCell()
            .storeUint(0xf8a7ea5, 32)
            .storeUint(0, 64) // op, queryId
            .storeCoins(jetton_amount)
            .storeAddress(to)
            .storeAddress(responseAddress)
            .storeMaybeRef(customPayload)
            .storeCoins(forward_ton_amount)
            .storeMaybeRef(forwardPayload)
            .endCell();
    }

    async sendTransfer(
        provider: ContractProvider,
        via: Sender,
        jettonAmount: bigint,
        toAddress: Address,
        responseAddress: Address,
        queryId: number = Date.now(),
        value: bigint = toNano('0.1'),
        forwardAmount?: bigint,
        forwardPayload?: Builder
    ) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Dao.sendJettonMessage({
                jettonAmount,
                toAddress,
                responseAddress,
                forwardAmount,
                queryId,
                forwardPayload,
            }),
            value,
        });
    }

    async sendFullTransfer(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        jetton_amount: bigint,
        to: Address,
        responseAddress: Address,
        customPayload: Cell | null,
        forward_ton_amount: bigint,
        forwardPayload: Cell | null
    ) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: JettonWallet.transferMessage(
                jetton_amount,
                to,
                responseAddress,
                customPayload,
                forward_ton_amount,
                forwardPayload
            ),
            value: value,
        });
    }
    /*
      burn#595f07bc query_id:uint64 amount:(VarUInteger 16)
                    response_destination:MsgAddress custom_payload:(Maybe ^Cell)
                    = InternalMsgBody;
    */
    static burnMessage(jetton_amount: bigint, responseAddress: Address, customPayload: Cell | null) {
        return beginCell()
            .storeUint(0x595f07bc, 32)
            .storeUint(0, 64) // op, queryId
            .storeCoins(jetton_amount)
            .storeAddress(responseAddress)
            .storeMaybeRef(customPayload)
            .endCell();
    }

    async sendBurn(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        jetton_amount: bigint,
        responseAddress: Address,
        customPayload: Cell | null
    ) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: JettonWallet.burnMessage(jetton_amount, responseAddress, customPayload),
            value: value,
        });
    }
    /*
      withdraw_tons#107c49ef query_id:uint64 = InternalMsgBody;
    */
    static withdrawTonsMessage() {
        return beginCell()
            .storeUint(0x6d8e5e3c, 32)
            .storeUint(0, 64) // op, queryId
            .endCell();
    }

    async sendWithdrawTons(provider: ContractProvider, via: Sender) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: JettonWallet.withdrawTonsMessage(),
            value: toNano('0.1'),
        });
    }
    /*
      withdraw_jettons#10 query_id:uint64 wallet:MsgAddressInt amount:Coins = InternalMsgBody;
    */
    static withdrawJettonsMessage(from: Address, amount: bigint) {
        return beginCell()
            .storeUint(0x768a50b2, 32)
            .storeUint(0, 64) // op, queryId
            .storeAddress(from)
            .storeCoins(amount)
            .storeMaybeRef(null)
            .endCell();
    }

    async sendWithdrawJettons(provider: ContractProvider, via: Sender, from: Address, amount: bigint) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: JettonWallet.withdrawJettonsMessage(from, amount),
            value: toNano('0.1'),
        });
    }

    /*
      vote query_id:uint64 voting_address:MsgAddressInt expiration_date:uint48 vote:Bool need_confirmation:Bool = InternalMsgBody;
    */
    static voteMessage(votingAddress: Address, expirationDate: Date, vote: boolean, queryId: number = Date.now()) {
        return beginCell()
            .storeUint(0x69fb306c, 32)
            .storeUint(queryId, 64)
            .storeAddress(votingAddress)
            .storeUint(Math.round(expirationDate.getTime() / 1000), 48)
            .storeBit(vote)
            .endCell();
    }

    async sendVote(
        provider: ContractProvider,
        via: Sender,
        voting_address: Address,
        expiration_date: Date,
        vote: boolean,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: JettonWallet.voteMessage(voting_address, expiration_date, vote, queryId),
            value: toNano('0.1'),
        });
    }

    async getVotedWeight(provider: ContractProvider, voting_id: bigint, expiration_date: bigint) {
        let state = await provider.getState();
        if (state.state.type !== 'active') {
            return 0n;
        }
        let res = await provider.get('get_voted_weight', [
            { type: 'int', value: voting_id },
            { type: 'int', value: expiration_date },
        ]);
        return res.stack.readBigNumber();
    }
    async getVoteControllerAddress(provider: ContractProvider, voting_address: Address): Promise<Address> {
        const res = await provider.get('get_vote_controller_address', [
            { type: 'slice', cell: beginCell().storeAddress(voting_address).endCell() },
        ]);
        return res.stack.readAddress();
    }
}
